import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  selectClient,
  selectSession,
  selectClientOnboardingSlideProgress,
  putClientOnboardingSlideProgressByClientIdRequest,
  selectSessionAdvisor,
} from '@/model';
import { SessionType, newUuid } from '@shared';
import { CLIENT_STARTED_REVIEW } from '@client';
import { useAdvisorOnboarding } from './useAdvisorOnboarding';

interface UseResultsReviewProgressOptions {
  clientId?: string;
  isAlgorithmFinishedRunning: boolean;
  hasAlgorithmError: boolean;
}

export function useResultsReviewProgress({
  clientId,
  isAlgorithmFinishedRunning,
  hasAlgorithmError,
}: UseResultsReviewProgressOptions) {
  const dispatch = useDispatch();
  const client = useSelector(selectClient);
  const onboardingSlideProgress = useSelector(selectClientOnboardingSlideProgress);
  const { sessionType } = useSelector(selectSession);
  const isAdvisorSession =
    sessionType === SessionType.advisor || sessionType === SessionType.wizard;
  const advisor = useSelector(selectSessionAdvisor);
  const { isStepCompleted, markStepAsCompleted } = useAdvisorOnboarding(
    isAdvisorSession ? (advisor?.advisorId ?? '') : '',
  );
  const hasMarkedReviewResults = useRef(false);

  useEffect(() => {
    if (
      isAdvisorSession &&
      !hasMarkedReviewResults.current &&
      !isStepCompleted('review-results') &&
      !client?.isSampleClient
    ) {
      hasMarkedReviewResults.current = true;
      markStepAsCompleted('review-results');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire once per mount when session type is known
  }, [sessionType, client?.isSampleClient]);

  useEffect(() => {
    if (
      !client ||
      !isAlgorithmFinishedRunning ||
      (onboardingSlideProgress && onboardingSlideProgress.hasClientStartedOnboarding) ||
      sessionType !== SessionType.client ||
      hasAlgorithmError
    ) {
      return;
    }
    const clientOnboardingSlideProgressId =
      onboardingSlideProgress?.clientOnboardingSlideProgressId ?? newUuid();
    const updateClientTags = [...(client.clientTags || []), CLIENT_STARTED_REVIEW];
    dispatch(
      putClientOnboardingSlideProgressByClientIdRequest({
        clientOnboardingSlideProgressId,
        clientId: clientId ?? client.clientId,
        hasClientStartedOnboarding: true,
        clientTags: updateClientTags,
      }),
    );
  }, [
    dispatch,
    clientId,
    client,
    onboardingSlideProgress?.hasClientStartedOnboarding,
    onboardingSlideProgress?.clientOnboardingSlideProgressId,
    isAlgorithmFinishedRunning,
    hasAlgorithmError,
    sessionType,
  ]);
}
